import { twMerge } from 'tailwind-merge';
import { CallRecordKey } from 'flow/types/EntityKeys';
import { homeActionUrlPath, voiceMessageUrlPath } from 'flow/utils';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { setVoiceMessageRead } from 'flow/actions/extension-actions';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';
import {
    selectCallRecordRecordingDurationSeconds,
    selectCallRecordRecordingPlaybackUrl,
    selectCallRecordVoiceMessage,
} from 'flow/selectors/call-records-selectors';
import AudioPlayerV2 from 'flow/components/pbx/v2/misc/AudioPlayerV2';
import BadgeVoiceMessageUnread from 'flow/components/pbx/v2/misc/BadgeVoiceMessageUnread';

interface Props {
    callRecordKey: CallRecordKey;
    selected?: boolean;
    // Called when the user plays/pauses a *recording*; voice messages keep their own handling.
    onClickPlayPause?: (value: 'play' | 'pause') => void;
    // For a recording only: 'none' in lists; see AudioPlayerV2. A voicemail
    // never loads before Play, since home marks it listened when it is
    // fetched, and it must stay unread until the user plays it.
    preload?: 'metadata' | 'none';
}

const CallRecordAudioPlayerV2 = ({
    callRecordKey,
    selected,
    onClickPlayPause,
    preload,
}: Props) => {
    const dispatch = useAppDispatch();
    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);
    const playbackUrl = useAppSelector((state) =>
        selectCallRecordRecordingPlaybackUrl(state, callRecordKey),
    );
    const recordingDuration = useAppSelector((state) =>
        selectCallRecordRecordingDurationSeconds(state, callRecordKey),
    );
    const voiceMessage = useAppSelector((state) =>
        selectCallRecordVoiceMessage(state, callRecordKey),
    );

    if (playbackUrl) {
        return (
            <AudioPlayerV2
                url={homeActionUrlPath(playbackUrl)}
                duration={recordingDuration}
                onClickPlayPause={onClickPlayPause}
                preload={preload}
                className={twMerge(
                    'group-hover:bg-gray-300',
                    selected && 'bg-gray-300',
                )}
            />
        );
    }

    const voiceMessageUniqueId = voiceMessage?.get('uniqueId');

    if (voiceMessageUniqueId) {
        const voiceMessageIsUnread =
            voiceMessage?.get('voiceMessageState') === 'unread';
        const handlePlayPause = (value: 'play' | 'pause') => {
            if (value === 'play' && voiceMessageIsUnread) {
                dispatch(
                    setVoiceMessageRead(
                        ownExtensionKey,
                        callRecordKey,
                        voiceMessageUniqueId,
                    ),
                );
            }
        };

        return (
            <div className="relative grow">
                <BadgeVoiceMessageUnread visible={voiceMessageIsUnread} />
                <AudioPlayerV2
                    url={voiceMessageUrlPath(
                        ownExtensionKey,
                        voiceMessageUniqueId,
                    )}
                    duration={voiceMessage?.get('durationInSeconds')}
                    onClickPlayPause={handlePlayPause}
                    preload="none"
                    className={twMerge(
                        'group-hover:bg-gray-300',
                        selected && 'bg-gray-300',
                    )}
                />
            </div>
        );
    }

    return null;
};

export default CallRecordAudioPlayerV2;
